/**
 * ScheduleReleasePage - Schedule future track releases
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, Music, ArrowLeft, Plus, Trash2,
  Check, X, Bell, Upload
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Scheduled Release Card
const ScheduledReleaseCard = ({ release, onDelete }) => {
  const releaseDate = new Date(release.scheduled_date);
  const isUpcoming = releaseDate > new Date();
  
  return (
    <div className="bg-[#12101A] rounded-2xl border border-white/5 p-4">
      <div className="flex gap-4">
        <div className="w-16 h-16 rounded-xl bg-white/10 overflow-hidden flex-shrink-0">
          {release.artwork ? (
            <img src={release.artwork} alt={release.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className="w-6 h-6 text-[#71717A]" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold truncate">{release.title}</h3>
          <p className="text-[#71717A] text-sm">{release.genre}</p>
          <div className="flex items-center gap-4 mt-2 text-sm">
            <span className="flex items-center gap-1 text-[#00F0FF]">
              <Calendar className="w-4 h-4" />
              {releaseDate.toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1 text-[#A1A1AA]">
              <Clock className="w-4 h-4" />
              {releaseDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            isUpcoming ? 'bg-[#00F0FF]/20 text-[#00F0FF]' : 'bg-green-500/20 text-green-400'
          }`}>
            {isUpcoming ? 'Scheduled' : 'Released'}
          </span>
          {isUpcoming && (
            <button
              onClick={() => onDelete(release.id)}
              className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ScheduleReleasePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const [scheduledReleases, setScheduledReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    const fetchScheduledReleases = async () => {
      if (!isAuthenticated) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // For now, use mock data since the endpoint might not exist
        // In production, this would fetch from the API
        setScheduledReleases([]);
        
      } catch (error) {
        console.error('Error fetching scheduled releases:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchScheduledReleases();
    }
  }, [isAuthenticated, authLoading]);

  const handleDeleteSchedule = async (releaseId) => {
    try {
      setScheduledReleases(prev => prev.filter(r => r.id !== releaseId));
    } catch (error) {
      console.error('Error deleting scheduled release:', error);
    }
  };

  if (!authLoading && !isAuthenticated) {
    navigate('/auth');
    return null;
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-[#FF007F] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-white/5"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
              Schedule Release
            </h1>
          </div>
        </div>
      </div>

      <div className="px-4 py-6">
        {/* Info Banner */}
        <div className="bg-gradient-to-r from-[#FF007F]/20 to-[#00F0FF]/20 rounded-2xl p-5 border border-[#FF007F]/30 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#FF007F]/20 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-[#FF007F]" />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">Schedule Your Drops</h3>
              <p className="text-[#A1A1AA] text-sm">
                Plan your releases ahead of time. Upload your track now and set a release date. 
                Your fans will be notified when it drops!
              </p>
            </div>
          </div>
        </div>

        {/* Schedule New Release Button */}
        <button
          onClick={() => navigate('/upload?schedule=true')}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 mb-8 hover:opacity-90 transition-opacity"
        >
          <Plus className="w-5 h-5" />
          Schedule New Release
        </button>

        {/* Scheduled Releases List */}
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-[#00F0FF]" />
            Upcoming Releases
          </h2>
          
          {scheduledReleases.length > 0 ? (
            <div className="space-y-4">
              {scheduledReleases.map((release) => (
                <ScheduledReleaseCard
                  key={release.id}
                  release={release}
                  onDelete={handleDeleteSchedule}
                />
              ))}
            </div>
          ) : (
            <div className="bg-[#12101A] rounded-2xl border border-white/5 p-8 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-[#52525B]" />
              <h3 className="text-white font-semibold mb-2">No Scheduled Releases</h3>
              <p className="text-[#71717A] text-sm mb-4">
                You don&apos;t have any upcoming releases scheduled yet.
              </p>
              <button
                onClick={() => navigate('/upload?schedule=true')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FF007F] text-white font-medium"
              >
                <Upload className="w-4 h-4" />
                Upload & Schedule
              </button>
            </div>
          )}
        </div>

        {/* Tips Section */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-[#FF007F]" />
            Release Tips
          </h2>
          <div className="bg-[#12101A] rounded-2xl border border-white/5 p-5">
            <ul className="space-y-3 text-[#A1A1AA] text-sm">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                Schedule releases for Friday at midnight for maximum visibility
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                Promote on social media before your release date
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                Your followers will be notified when your track drops
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                High-quality artwork increases engagement by 40%
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleReleasePage;
